/**
 * Job Status Tab for Demo Admin
 *
 * Displays recent demo data generation and purge jobs with real-time status polling
 */

import React from 'react';
import {
  Box,
  Typography,
  CircularProgress,
  Paper,
  Chip,
  Divider,
  Alert
} from '@mui/material';
import { STATUS_COLORS } from '../constants';

/**
 * Job Status Tab Component
 * @param {Object} props - Component props
 * @param {Array} props.recentRuns - Array of recent DemoRun objects
 * @param {boolean} props.statusLoading - Loading state for status fetch
 */
const JobStatusTab = ({ recentRuns, statusLoading }) => {
  return (
    <Paper sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6" sx={{ color: '#7C3AED' }}>
          Recent Jobs
        </Typography>
        {statusLoading && <CircularProgress size={20} />}
      </Box>

      {recentRuns.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
          No recent jobs. Generate or purge demo data to see job history here.
        </Typography>
      ) : (
        recentRuns.map((run, index) => (
          <Box key={run.runId} sx={{ mb: index < recentRuns.length - 1 ? 2 : 0 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {run.operation} - {run.scopeType}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {run.startDate} to {run.endDate}
                </Typography>
              </Box>
              <Chip
                label={run.status}
                size="small"
                sx={{
                  backgroundColor: STATUS_COLORS[run.status] || '#9E9E9E',
                  color: 'white'
                }}
              />
            </Box>

            {run.lastLogLine && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                {run.lastLogLine}
              </Typography>
            )}

            {run.errorMessage && (
              <Alert severity="error" sx={{ mt: 1 }}>
                {run.errorMessage}
              </Alert>
            )}

            {run.writtenAtomic && (
              <Typography variant="caption" color="success.main">
                ✓ Written: {run.writtenAtomic} records
              </Typography>
            )}

            {run.deletedAtomic && (
              <Typography variant="caption" color="error.main">
                ✗ Deleted: {run.deletedAtomic} records
              </Typography>
            )}

            {index < recentRuns.length - 1 && <Divider sx={{ mt: 2 }} />}
          </Box>
        ))
      )}
    </Paper>
  );
};

export default JobStatusTab;
